import { useState, useEffect } from "react";
import {
    fetchCategories,
    fetchAllEntries,
    fetchResults,
    type Category,
    type NominationEntry,
    type ResultRow,
} from "../api/tcg";
import PieChart, { type Slice } from "./pieChart.component";
import AdminMerge from "./adminMerge.component";
import AdminBlocklist from "./adminBlocklist.component";
import AdminDeletedNominations from "./adminDeletedNominations.component";

type ViewType = "standings" | "results";
type AdminViewRow = NominationEntry | ResultRow;

function nominationLabel(row: NominationEntry): string {
    return row.display_text || row.normalized_text || "";
}

// Baut die Diagramm-Segmente je Ansicht.
function buildSlices(view: ViewType, rows: AdminViewRow[]): Slice[] {
    if (view === "standings") {
        const nominations = rows as NominationEntry[];
        const sorted = [...nominations].sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
        const top: Slice[] = sorted.slice(0, 5).map((r) => ({ label: nominationLabel(r), value: Number(r.count) || 0 }));
        const rest = sorted.slice(5).reduce((sum, r) => sum + (Number(r.count) || 0), 0);
        if (rest > 0) top.push({ label: "Übrige Nominierungen", value: rest, color: "#9AA0A6" });
        return top;
    }
    // results: die (bis zu) 5 Finalisten
    return [...(rows as ResultRow[])]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .slice(0, 5)
        .map((r) => ({ label: r.display_text, value: Number(r.votes) || 0 }));
}

// Tabelle: bei Nominierung nur die Top 5, beim Voting die Finalisten.
function tableRows(view: ViewType, rows: AdminViewRow[]): AdminViewRow[] {
    if (view === "standings") {
        return [...(rows as NominationEntry[])].sort((a, b) => (b.count ?? 0) - (a.count ?? 0)).slice(0, 5);
    }
    return rows;
}

function AdminViews({ token, fixedView }: { token: string; fixedView: ViewType }) {
    const [categories, setCategories] = useState<Category[]>([]);
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const view = fixedView; // feste Ansicht je Bereich – kein Umschalter mehr
    const [rows, setRows] = useState<AdminViewRow[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadedCategoryId, setLoadedCategoryId] = useState<number | null>(null);
    const [blocklistReloadKey, setBlocklistReloadKey] = useState(0);
    const [deletedReloadKey, setDeletedReloadKey] = useState(0);

    useEffect(() => {
        fetchCategories()
            .then((cats) => {
                const active = cats.filter((c) => c.group_key === "product" || c.group_key === "community");
                setCategories(active);
                if (active.length > 0) setCategoryId(active[0].id);
            })
            .catch((err) => setError(err instanceof Error ? err.message : "Fehler."));
    }, []);

    async function load() {
        if (categoryId === null) return;
        setLoading(true);
        setError(null);
        try {
            if (view === "standings") setRows(await fetchAllEntries(token, categoryId));
            else setRows(await fetchResults(token, categoryId));
            setLoadedCategoryId(categoryId);
            if (view === "standings") {
                setBlocklistReloadKey((current) => current + 1);
                setDeletedReloadKey((current) => current + 1);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Konnte Daten nicht laden.");
            setRows([]);
            setLoadedCategoryId(null);
        } finally {
            setLoading(false);
        }
    }

    function handleCategoryChange(nextCategoryId: number) {
        setCategoryId(nextCategoryId);
        setRows([]);
        setLoadedCategoryId(null);
        setError(null);
    }

    function handleBlocklistChanged() {
        setDeletedReloadKey((current) => current + 1);
    }

    const hasLoadedSelectedCategory =
        categoryId !== null && loadedCategoryId === categoryId;

    return (
        <section className="admin-card">
            <h2 className="admin-card-title">
                {view === "standings" ? "Nominierungs-Stände" : "Voting-Ergebnisse"}
            </h2>
            <p className="admin-note" style={{ marginBottom: "18px" }}>
                {view === "standings"
                    ? "Wähle eine Kategorie und klick „Anzeigen“, um die aktuellen Nominierungs-Stände zu laden."
                    : "Wähle eine Kategorie und klick „Anzeigen“, um die aktuellen Voting-Ergebnisse zu laden."}
            </p>

            <div className="admin-control-row">
                <label className="admin-label">Kategorie</label>
                <select
                    className="admin-input"
                    value={categoryId ?? ""}
                    onChange={(e) => handleCategoryChange(Number(e.target.value))}
                >
                    {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </div>
            <div className="admin-actions">
                <button className="admin-btn-primary" onClick={load} disabled={loading}>
                    {loading ? "Lädt…" : "Anzeigen"}
                </button>
            </div>

            {error && <p className="admin-error">{error}</p>}

            {hasLoadedSelectedCategory ? (
                <>
                    {rows.length > 0 ? (
                        <div className="admin-views-layout">
                            <PieChart slices={buildSlices(view, rows)} />
                            <div className="admin-views-table">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            {view === "standings" && (<><th>Eintrag</th><th>Anzahl</th></>)}
                                            {view === "results" && (<><th>Position</th><th>Finalist</th><th>Stimmen</th></>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableRows(view, rows).map((r, i) => (
                                            <tr key={i}>
                                                {view === "standings" && (
                                                    <>
                                                        <td>{nominationLabel(r as NominationEntry)}</td>
                                                        <td>{(r as NominationEntry).count}</td>
                                                    </>
                                                )}
                                                {view === "results" && (
                                                    <>
                                                        <td>{(r as ResultRow).position}</td>
                                                        <td>{(r as ResultRow).display_text}</td>
                                                        <td>{(r as ResultRow).votes}</td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <p className="admin-note">Für diese Kategorie sind aktuell keine Daten vorhanden.</p>
                    )}
                    {view === "standings" && categoryId !== null && rows.length > 0 && (
                        <AdminMerge
                            token={token}
                            categoryId={categoryId}
                            entries={rows as NominationEntry[]}
                            onEntriesChanged={load}
                        />
                    )}
                </>
            ) : (
                !loading && <p className="admin-note">Keine Daten – wähle Kategorie und klick „Anzeigen".</p>
            )}
            {view === "standings" && hasLoadedSelectedCategory && categoryId !== null && (
                <AdminDeletedNominations
                    key={`deleted-${categoryId}`}
                    token={token}
                    categoryId={categoryId}
                    reloadKey={deletedReloadKey}
                    onChanged={load}
                />
            )}
            {view === "standings" && hasLoadedSelectedCategory && categoryId !== null && (
                <AdminBlocklist
                    key={categoryId}
                    token={token}
                    categoryId={categoryId}
                    reloadKey={blocklistReloadKey}
                    onChanged={handleBlocklistChanged}
                />
            )}
        </section>
    );
}

export default AdminViews;